import type { UiState, IndexUiState } from 'instantsearch.js';

import { URL_ARRAY_SEPARATOR } from '../constants';

import { getItem } from './memoryStorage';

type Mapping = Record<string, string>;

export function mapRefinementListToRoute(
  refinementList: IndexUiState['refinementList'],
  mapping: Mapping,
  arraySeparator = URL_ARRAY_SEPARATOR
): Record<string, string> {
  if (!refinementList) {
    return {};
  }

  return Object.keys(refinementList).reduce((acc, key) => {
    const routeKey = mapping[key] || key;
    return {
      ...acc,
      [routeKey]: refinementList[key].join(arraySeparator),
    };
  }, {});
}

export function mapMenuToRoute(
  menu: IndexUiState['menu'],
  mapping: Mapping
): Record<string, string> {
  if (!menu) {
    return {};
  }
  return Object.keys(menu).reduce((acc, key) => {
    const routeKey = mapping[key] || key;
    return {
      ...acc,
      [routeKey]: menu[key],
    };
  }, {});
}

export interface StateToRouteAlgoliaOptions {
  refinementList?: Mapping;
  menu?: Mapping;
  numericMenu?: Mapping;
  page?: string;
  query?: string;
  geoSearch?: string;
}

interface StateToRouteOptions {
  arraySeparator?: string;
}

function stateToRoute(
  uiState: UiState,
  indexName: string,
  {
    refinementList: refinementListMapping = {},
    menu: menuMapping = {},
    numericMenu: numericMenuMapping = {},
    page: pageMapping = 'page',
    query: queryMapping = 'query',
    geoSearch: geoSearchMapping = 'geoSearch',
  }: StateToRouteAlgoliaOptions = {},
  { arraySeparator = URL_ARRAY_SEPARATOR }: StateToRouteOptions = {}
) {
  const indexUiState = uiState[indexName];
  if (!indexUiState) {
    return {};
  }

  const { menu, numericMenu, refinementList, page, query, geoSearch } = indexUiState;
  let geoSearchRouteParam: string = '';

  if (geoSearch) {
    const boundingBoxes = geoSearch.boundingBox as unknown as Array<number[]>;
    const params: string[] = [];

    boundingBoxes.forEach((location) => {
      const value = location.join(',');
      const locationData = getItem(value);

      const locationLabel = locationData?.label || value;
      const locationOriginalValue = locationData?.originalValue || value;
      const locationRadiusInKilometers = locationData?.radiusInKilometers || 0;
      const locationLabelWithRadius = locationData?.labelWithRadius || locationLabel;

      params.push(
        `${value}|${locationLabel}|${locationOriginalValue}|${locationRadiusInKilometers}|${locationLabelWithRadius}`
      );
    });

    geoSearchRouteParam = params.join('__');
  }

  const routeState = {
    ...(query ? { [queryMapping]: query } : {}),
    ...(geoSearchRouteParam ? { [geoSearchMapping]: geoSearchRouteParam } : {}),
    ...mapRefinementListToRoute(refinementList, refinementListMapping, arraySeparator),
    ...mapMenuToRoute(menu, menuMapping),
    ...mapMenuToRoute(numericMenu, numericMenuMapping),
    [pageMapping]: page || 1,
  };

  if (routeState[pageMapping] === 1) {
    delete routeState[pageMapping];
  }

  return routeState;
}

export default stateToRoute;
